'use client';

import Link from 'next/link';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import type { BlogPost } from '@/features/marketing/constants/blog-posts';
import LandingNavbar from './LandingNavbar';
import FooterSection from './FooterSection';
import ScrollReveal from '@/features/preview/components/shared/ScrollReveal';

interface BlogPostProps {
  post: BlogPost;
}

export default function BlogPostLayout({ post }: BlogPostProps) {
  const { t, locale } = useLocale();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <LandingNavbar surface="dark" />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <Link
              href="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            {/* Category badge */}
            <div className="mb-4">
              <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-500 text-xs font-medium uppercase tracking-wider">
                {t(post.category)}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent mb-4">
              {t(post.title)}
            </h1>

            {/* Description */}
            <p className="text-lg text-gray-400 leading-relaxed mb-6">{t(post.description)}</p>

            {/* Date */}
            <time className="text-sm text-gray-600">
              {new Date(post.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </time>
          </ScrollReveal>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-t border-white/5" />
      </div>

      {/* Content */}
      {post.sections.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-16">
            {post.sections.map((section, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key -- CMS blog sections have no stable id; append-only display, never reordered or filtered at render time
              <ScrollReveal key={index} delay={index * 60}>
                <article>
                  <h2 className="text-2xl font-bold text-white mb-4">{t(section.heading)}</h2>
                  <p className="text-gray-400 leading-relaxed mb-6">{t(section.content)}</p>

                  {section.bullets && (
                    <ul className="space-y-3">
                      {section.bullets[locale].map((bullet, i) => (
                        // eslint-disable-next-line @eslint-react/no-array-index-key -- CMS bullet strings have no stable id; append-only display, never reordered
                        <li key={i} className="flex items-start gap-3">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                          <span className="text-gray-300 leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* Feature highlight placeholder — filled by content agents */}
      {post.sections.length === 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-xl bg-white/[0.02] border border-white/5 p-8 text-center">
              <p className="text-gray-500 text-sm">Content coming soon.</p>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <ScrollReveal>
            <div className="rounded-2xl bg-gradient-to-b from-amber-500/10 to-amber-500/5 border border-amber-500/20 p-10">
              <h2 className="text-2xl font-bold text-white mb-3">{t(post.cta)}</h2>
              <p className="text-gray-400 mb-8">14-day free trial. No credit card required.</p>
              <Link
                href="/auth/register"
                className={cn(
                  'group inline-flex items-center gap-2 px-8 py-3.5 rounded-lg',
                  'bg-amber-500 text-black font-semibold',
                  'hover:bg-amber-400 transition-all duration-200',
                  'shadow-[0_0_30px_rgba(245,158,11,0.3)]',
                  'hover:shadow-[0_0_40px_rgba(245,158,11,0.5)]'
                )}
              >
                Start Your Free Trial
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
